import { WebPCodec } from '@playcanvas/splat-transform';
import { Color, createGraphicsDevice } from 'playcanvas';

import { registerAnnotationsEvents } from './annotations';
import { registerCameraPosesEvents } from './camera-poses';
import { registerConsoleLog, watchWebglContext } from './console-log';
import { registerContentEvents } from './content';
import { registerContentDragDrop } from './content-drag-drop';
import { registerDocEvents } from './doc';
import { EditHistory } from './edit-history';
import { registerEditorEvents } from './editor';
import { registerEnvironmentEvents } from './environment';
import { Events } from './events';
import { initFileHandler } from './file-handler';
import { registerIframeApi } from './iframe-api';
import { registerPlySequenceEvents } from './ply-sequence';
import { registerPublishEvents } from './publish';
import { registerRenderEvents } from './render';
import { Scene } from './scene';
import { getSceneConfig } from './scene-config';
import { registerSelectionEvents } from './selection';
import { registerShareEvents } from './share';
import { ShortcutManager } from './shortcut-manager';
import { registerStudioPublishEvents } from './studio-publish';
import { registerTimelineEvents } from './timeline';
import { BoxSelection } from './tools/box-selection';
import { BrushSelection } from './tools/brush-selection';
import { EyedropperSelection } from './tools/eyedropper-selection';
import { FloodSelection } from './tools/flood-selection';
import { LassoSelection } from './tools/lasso-selection';
import { MeasureTool } from './tools/measure-tool';
import { MoveTool } from './tools/move-tool';
import { PolygonSelection } from './tools/polygon-selection';
import { RectSelection } from './tools/rect-selection';
import { RotateTool } from './tools/rotate-tool';
import { ScaleTool } from './tools/scale-tool';
import { SphereSelection } from './tools/sphere-selection';
import { ToolManager } from './tools/tool-manager';
import { registerTrackManagerEvents } from './track-manager';
import { registerTransformHandlerEvents } from './transform-handler';
import { EditorUI } from './ui/editor';
import { localizeInit } from './ui/localization';
import { registerShareDialog } from './ui/share-dialog';
import { registerStudioPublishDialog } from './ui/studio-publish-dialog';
import { registerWaterEvents } from './water';

declare global {
    interface LaunchParams {
        readonly files: FileSystemFileHandle[];
    }

    interface Window {
        launchQueue: {
            setConsumer: (callback: (launchParams: LaunchParams) => void) => void;
        };
        scene: Scene;
    }
}

const getURLArgs = () => {
    // extract settings from command line in non-prod builds only
    const config = {};

    const apply = (key: string, value: string) => {
        let obj: any = config;
        key.split('.').forEach((k, i, a) => {
            if (i === a.length - 1) {
                obj[k] = value;
            } else {
                if (!obj.hasOwnProperty(k)) {
                    obj[k] = {};
                }
                obj = obj[k];
            }
        });
    };

    const params = new URLSearchParams(window.location.search.slice(1));
    params.forEach((value: string, key: string) => {
        apply(key, value);
    });

    return config;
};

const main = async () => {
    // root events object
    const events = new Events();

    // first, so warnings raised during startup reach the Console tab too
    registerConsoleLog(events);

    // url
    const url = new URL(window.location.href);

    // presentation / view mode: hides all editing UI and presents the scene
    // read-only with clickable hotspots (for client-facing delivery)
    const viewMode = url.searchParams.has('view');
    events.function('viewMode', () => viewMode);
    if (viewMode) {
        document.body.classList.add('view-mode');
    }

    // edit history
    const editHistory = new EditHistory(events);

    // expose edit history queue so subsystems (e.g. transform handlers) can serialize their
    // own async work onto the same chain that gates add/undo/redo.
    events.function('edit.queue', (fn: () => Promise<void>) => editHistory.queue(fn));

    // init localization
    await localizeInit();

    // Configure WebP WASM for SOG format (used for both reading and writing)
    WebPCodec.wasmUrl = new URL('static/lib/webp/webp.wasm', document.baseURI).toString();

    // register events that only need the events object (before UI is created)
    registerTimelineEvents(events);
    registerCameraPosesEvents(events);
    registerTransformHandlerEvents(events);
    registerPlySequenceEvents(events);
    registerPublishEvents(events);
    registerShareEvents(events);
    registerShareDialog(events);
    registerStudioPublishEvents(events);
    registerStudioPublishDialog(events);
    registerIframeApi(events);

    // initialize shortcuts
    const shortcutManager = new ShortcutManager(events);
    events.function('shortcutManager', () => shortcutManager);

    // editor ui
    const editorUI = new EditorUI(events);
    watchWebglContext(editorUI.canvas);

    // create the graphics device
    const graphicsDevice = await createGraphicsDevice(editorUI.canvas, {
        deviceTypes: ['webgl2'],
        antialias: false,
        depth: false,
        stencil: false,
        xrCompatible: false,
        powerPreference: 'high-performance'
    });

    const overrides = [
        getURLArgs()
    ];

    // resolve scene config
    const sceneConfig = getSceneConfig(overrides);

    // construct the manager
    const scene = new Scene(
        events,
        sceneConfig,
        editorUI.canvas,
        graphicsDevice
    );

    // colors
    const bgClr = new Color();
    const selectedClr = new Color();
    const unselectedClr = new Color();
    const lockedClr = new Color();

    const setClr = (target: Color, value: Color, event: string) => {
        if (!target.equals(value)) {
            target.copy(value);
            events.fire(event, target);
        }
    };

    const setBgClr = (clr: Color) => {
        setClr(bgClr, clr, 'bgClr');
    };
    const setSelectedClr = (clr: Color) => {
        setClr(selectedClr, clr, 'selectedClr');
    };
    const setUnselectedClr = (clr: Color) => {
        setClr(unselectedClr, clr, 'unselectedClr');
    };
    const setLockedClr = (clr: Color) => {
        setClr(lockedClr, clr, 'lockedClr');
    };

    events.on('setBgClr', (clr: Color) => {
        setBgClr(clr);
    });
    events.on('setSelectedClr', (clr: Color) => {
        setSelectedClr(clr);
    });
    events.on('setUnselectedClr', (clr: Color) => {
        setUnselectedClr(clr);
    });
    events.on('setLockedClr', (clr: Color) => {
        setLockedClr(clr);
    });

    events.function('bgClr', () => {
        return bgClr;
    });
    events.function('selectedClr', () => {
        return selectedClr;
    });
    events.function('unselectedClr', () => {
        return unselectedClr;
    });
    events.function('lockedClr', () => {
        return lockedClr;
    });

    events.on('bgClr', (clr: Color) => {
        const cnv = (v: number) => `${Math.max(0, Math.min(255, (v * 255))).toFixed(0)}`;
        document.body.style.backgroundColor = `rgba(${cnv(clr.r)},${cnv(clr.g)},${cnv(clr.b)},1)`;
    });
    events.on('selectedClr', (clr: Color) => {
        scene.forceRender = true;
    });
    events.on('unselectedClr', (clr: Color) => {
        scene.forceRender = true;
    });
    events.on('lockedClr', (clr: Color) => {
        scene.forceRender = true;
    });

    // initialize colors from application config
    const toColor = (value: { r: number, g: number, b: number, a: number }) => {
        return new Color(value.r, value.g, value.b, value.a);
    };
    setBgClr(toColor(sceneConfig.bgClr));
    setSelectedClr(toColor(sceneConfig.selectedClr));
    setUnselectedClr(toColor(sceneConfig.unselectedClr));
    setLockedClr(toColor(sceneConfig.lockedClr));

    // create the mask selection canvas
    const maskCanvas = document.createElement('canvas');
    const maskContext = maskCanvas.getContext('2d');
    maskCanvas.setAttribute('id', 'mask-canvas');
    maskContext.globalCompositeOperation = 'copy';

    const mask = {
        canvas: maskCanvas,
        context: maskContext
    };

    // tool manager
    const toolManager = new ToolManager(events);
    toolManager.register('rectSelection', new RectSelection(events, editorUI.toolsContainer.dom));
    toolManager.register('brushSelection', new BrushSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('floodSelection', new FloodSelection(events, editorUI.toolsContainer.dom, mask, editorUI.canvasContainer));
    toolManager.register('polygonSelection', new PolygonSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('lassoSelection', new LassoSelection(events, editorUI.toolsContainer.dom, mask));
    toolManager.register('sphereSelection', new SphereSelection(events, scene, editorUI.canvasContainer));
    toolManager.register('boxSelection', new BoxSelection(events, scene, editorUI.canvasContainer));
    toolManager.register('eyedropperSelection', new EyedropperSelection(events, editorUI.toolsContainer.dom, editorUI.canvasContainer));
    toolManager.register('move', new MoveTool(events, scene));
    toolManager.register('rotate', new RotateTool(events, scene));
    toolManager.register('scale', new ScaleTool(events, scene));
    toolManager.register('measure', new MeasureTool(events, scene, editorUI.toolsContainer.dom, editorUI.canvasContainer));

    editorUI.toolsContainer.dom.appendChild(maskCanvas);

    window.scene = scene;

    // register events that need scene or other dependencies
    registerEditorEvents(events, editHistory, scene);
    registerSelectionEvents(events, scene);
    registerTrackManagerEvents(events, scene);
    registerAnnotationsEvents(events, scene);
    registerEnvironmentEvents(events, scene);
    registerContentEvents(events, scene);
    registerWaterEvents(events, scene);
    registerDocEvents(scene, events);
    registerRenderEvents(scene, events);
    // must run before initFileHandler: both attach capture-phase drag/drop
    // listeners to the same element, and this one needs first refusal so it
    // can claim content-palette drags before the file-drop handler sees them
    registerContentDragDrop(events, scene, editorUI.appContainer.dom);
    initFileHandler(scene, events, editorUI.appContainer.dom);

    // load async models
    scene.start();

    // branded loading overlay while a presented scene loads
    let viewLoader: HTMLElement = null;
    if (viewMode) {
        viewLoader = document.createElement('div');
        viewLoader.id = 'view-loader';
        viewLoader.innerHTML = `
            <div class="view-loader-mark">S</div>
            <div class="view-loader-spinner"></div>
            <div class="view-loader-text">Loading experience…</div>
        `;
        document.body.appendChild(viewLoader);
    }

    // update / replace the view-loader content
    const setLoaderText = (text: string) => {
        const el = viewLoader?.querySelector('.view-loader-text');
        if (el) {
            el.textContent = text;
        }
    };

    const showLoaderError = (message: string) => {
        if (!viewLoader) {
            return;
        }
        viewLoader.innerHTML = `
            <div class="view-loader-mark">S</div>
            <div class="view-loader-error-title">Couldn’t load this experience</div>
            <div class="view-loader-error-text">${message}</div>
            <button class="view-loader-retry">Try again</button>
        `;
        viewLoader.querySelector('.view-loader-retry')
        ?.addEventListener('click', () => window.location.reload());
    };

    // fetch a hosted scene with progress + a stall timeout so the view loader is
    // never a silent spinner. Returns a named File ready to hand to `import`.
    const fetchSceneWithProgress = async (fileUrl: string, filename: string): Promise<File> => {
        const controller = new AbortController();
        const STALL_MS = 30000;
        let timer = setTimeout(() => controller.abort(), STALL_MS);
        const resetStall = () => {
            clearTimeout(timer);
            timer = setTimeout(() => controller.abort(), STALL_MS);
        };

        try {
            const response = await fetch(fileUrl, { signal: controller.signal });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const total = Number(response.headers.get('content-length')) || 0;
            const reader = response.body?.getReader();
            if (!reader) {
                return new File([await response.blob()], filename);
            }

            const chunks: Uint8Array[] = [];
            let received = 0;
            for (;;) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }
                resetStall();
                chunks.push(value);
                received += value.length;
                setLoaderText(total ?
                    `Loading experience… ${Math.round((received / total) * 100)}%` :
                    `Loading experience… ${(received / (1024 * 1024)).toFixed(1)} MB`);
            }
            return new File(chunks as BlobPart[], filename);
        } finally {
            clearTimeout(timer);
        }
    };

    // handle load params
    const loadList = url.searchParams.getAll('load');
    const filenameList = url.searchParams.getAll('filename');
    let loadFailed = false;
    for (const [i, value] of loadList.entries()) {
        // searchParams.getAll already decodes once — do NOT decode again, or an
        // encoded path separator (Firebase URLs contain experiences%2F…) collapses
        // to a literal '/' and the download fails with HTTP 400.
        const decoded = value;
        const filename = i < filenameList.length ?
            filenameList[i] :
            // strip any query string / hash so the file extension is detectable
            // (hosted URLs often carry tokens, e.g. Firebase ?alt=media&token=…)
            decoded.split('/').pop().split('?')[0].split('#')[0];

        try {
            if (viewMode) {
                // stream with progress so clients see a live %, and so a stalled
                // download surfaces as an error instead of an endless spinner
                const contents = await fetchSceneWithProgress(decoded, filename);
                await events.invoke('import', [{ filename, contents }]);
            } else {
                await events.invoke('import', [{ filename, url: decoded }]);
            }
        } catch (err) {
            loadFailed = true;
            console.error('failed to load scene', err);
            if (viewMode) {
                showLoaderError('Please check the link and your connection, then try again.');
            }
            break;
        }
    }

    // Studio → Editor handoff: `?content=<raw splat url>` (e.g. a Studio-hosted
    // .compressed.ply). Distinct from `?load=`, which restores a saved .ssproj
    // project — this just opens the raw splat data for editing, same as
    // dragging a file in. Optional `?title=` sets the document name.
    if (loadList.length === 0) {
        const contentUrl = url.searchParams.get('content');
        if (contentUrl) {
            const filename = contentUrl.split('/').pop().split('?')[0].split('#')[0] || 'scene.ply';
            try {
                await events.invoke('import', [{ filename, url: contentUrl }]);
                const title = url.searchParams.get('title');
                if (title) {
                    events.fire('doc.setName', title);
                }
            } catch (err) {
                console.error('failed to load content', err);
            }
        }
    }

    // dismiss the loading overlay once the scene is ready (keep it as an error
    // panel if loading failed)
    if (viewMode && !loadFailed) {
        // clear any auto-selection so no editor highlight/gizmo shows in presentation
        events.fire('selection', null);
        if (viewLoader) {
            const loader = viewLoader;
            loader.style.opacity = '0';
            setTimeout(() => loader.remove(), 500);
        }

        // viewer chrome: a "drag to explore" hint (fades on first interaction),
        // a fullscreen toggle, a "Play tour" button (when the scene has
        // hotspots), and a subtle "Made with Simam" credit/CTA.
        const hasTour = ((events.invoke('annotations.list') as unknown[]) ?? []).length > 0;
        const chrome = document.createElement('div');
        chrome.id = 'view-chrome';
        chrome.innerHTML = `
            <div class="view-hint">
                <span class="view-hint-icon">↺</span>
                <span>Drag to explore</span>
            </div>
            ${hasTour ? '<button class="view-tour-start" title="Play guided tour">▶ Play tour</button>' : ''}
            <button class="view-fullscreen" title="Toggle fullscreen" aria-label="Toggle fullscreen">⛶</button>
            <a class="view-credit" href="https://simamdigital.com" target="_blank" rel="noopener">Made with <strong>Simam</strong></a>
        `;
        document.body.appendChild(chrome);

        // fade the hint away on first interaction (or after a few seconds)
        const hint = chrome.querySelector('.view-hint') as HTMLElement;
        const dismissHint = () => {
            if (hint) {
                hint.classList.add('view-hint-hidden');
            }
            window.removeEventListener('pointerdown', dismissHint);
            window.removeEventListener('wheel', dismissHint);
        };
        window.addEventListener('pointerdown', dismissHint);
        window.addEventListener('wheel', dismissHint, { passive: true });
        setTimeout(dismissHint, 5000);

        if (hasTour) {
            const tourBar = document.createElement('div');
            tourBar.id = 'view-tour-bar';
            tourBar.innerHTML = `
                <button class="view-tour-prev" title="Previous" aria-label="Previous">‹</button>
                <button class="view-tour-pause" title="Pause" aria-label="Pause">❚❚</button>
                <button class="view-tour-next" title="Next" aria-label="Next">›</button>
                <span class="view-tour-count"></span>
                <button class="view-tour-close" title="Close tour" aria-label="Close tour">×</button>
            `;
            tourBar.style.display = 'none';
            document.body.appendChild(tourBar);

            const countEl = tourBar.querySelector('.view-tour-count') as HTMLElement;
            const pauseBtn = tourBar.querySelector('.view-tour-pause') as HTMLElement;

            events.on('annotations.tourChanged', (state: { index: number, total: number, playing: boolean } | null) => {
                if (!state) {
                    tourBar.style.display = 'none';
                    return;
                }
                tourBar.style.display = 'flex';
                countEl.textContent = `${state.index + 1} / ${state.total}`;
                pauseBtn.textContent = state.playing ? '❚❚' : '▶';
                pauseBtn.title = state.playing ? 'Pause' : 'Resume';
            });

            chrome.querySelector('.view-tour-start')?.addEventListener('click', () => {
                dismissHint();
                events.fire('annotations.tourStart');
            });
            tourBar.querySelector('.view-tour-prev')?.addEventListener('click', () => events.fire('annotations.tourPrev'));
            tourBar.querySelector('.view-tour-next')?.addEventListener('click', () => events.fire('annotations.tourNext'));
            tourBar.querySelector('.view-tour-pause')?.addEventListener('click', () => events.fire('annotations.tourTogglePause'));
            tourBar.querySelector('.view-tour-close')?.addEventListener('click', () => events.fire('annotations.tourStop'));
        }

        // fullscreen toggle
        chrome.querySelector('.view-fullscreen')?.addEventListener('click', () => {
            if (document.fullscreenElement) {
                document.exitFullscreen();
            } else {
                document.documentElement.requestFullscreen?.();
            }
        });
    }


    // handle OS-based file association in PWA mode
    if ('launchQueue' in window) {
        window.launchQueue.setConsumer(async (launchParams: LaunchParams) => {
            for (const file of launchParams.files) {
                await events.invoke('import', [{
                    filename: file.name,
                    contents: await file.getFile()
                }]);
            }
        });
    }
};

export { main };
